import { NewDashboardOrgType } from 'helpers/repTypeHelper';

type RunXRankThresholds = { Sales?: number; Installs?: number; CS?: number };

const STANDARD_RANK_LEVELS = [1, 2, 3, 4, 5] as const;
const ATHLETE_RANK_LEVELS = [1, 2, 3, 4] as const;

const streetRankThresholds: Record<number, RunXRankThresholds> = {
  1: { Sales: 7, Installs: 3 },
  2: { Sales: 12, Installs: 5 },
  3: { Sales: 16, Installs: 7 },
  4: { Sales: 20, Installs: 10 },
  5: { Installs: 20 },
};

const blockRankThresholds: Record<number, RunXRankThresholds> = {
  1: { Installs: 5 },
  2: { Installs: 10 },
  3: { Installs: 15 },
  4: { Installs: 20 },
  5: { Installs: 25 },
};

const athleteRankThresholds: Record<number, RunXRankThresholds> = {
  1: { Installs: 4 },
  2: { Installs: 8 },
  3: { Installs: 12 },
  4: { Installs: 16 },
};

const chainExpertsRankThresholds: Record<number, RunXRankThresholds> = {
  1: { Installs: 6 },
  2: { Installs: 9 },
  3: { Installs: 12 },
  4: { Installs: 15 },
  5: { Installs: 20 },
};

const airAdsRankThresholds: Record<number, RunXRankThresholds> = {
  1: { Installs: 5 },
  2: { Installs: 8 },
  3: { Installs: 11 },
  4: { Installs: 14 },
  5: { Installs: 17 },
};

const airOrganicRankThresholds: Record<number, RunXRankThresholds> = {
  1: { Installs: 9 },
  2: { Installs: 12 },
  3: { Installs: 15 },
  4: { Installs: 18 },
  5: { Installs: 21 },
};

const getNextRankMap = (
  orgType: NewDashboardOrgType,
  sellsSunrun: boolean,
): Record<number, RunXRankThresholds> => {
  switch (orgType) {
    case 'BLOCK':
      return sellsSunrun ? blockRankThresholds : athleteRankThresholds;
    case 'CHAIN':
      return chainExpertsRankThresholds;
    case 'AIR':
      return sellsSunrun ? airAdsRankThresholds : airOrganicRankThresholds;
    case 'STREET':
    case 'UNKNOWN':
      return streetRankThresholds;
    default: {
      const exhaustiveCheck: never = orgType;
      return exhaustiveCheck;
    }
  }
};

const getRankLevels = (
  orgType: NewDashboardOrgType,
  sellsSunrun: boolean,
): readonly number[] => {
  if (orgType === 'BLOCK' && !sellsSunrun) {
    return ATHLETE_RANK_LEVELS;
  }
  return STANDARD_RANK_LEVELS;
};

const getMaxRankLevel = (
  orgType: NewDashboardOrgType,
  sellsSunrun: boolean,
): number => {
  const levels = getRankLevels(orgType, sellsSunrun);
  return levels[levels.length - 1];
};

const formatLevelLabel = (
  thresholds: RunXRankThresholds,
  level: number,
  orgType: NewDashboardOrgType,
): string => {
  if (orgType === 'STREET') {
    if (
      level === 4 &&
      thresholds.Sales != null &&
      thresholds.Installs != null
    ) {
      return `${thresholds.Sales} Sales + ${thresholds.Installs} Installs or 15 Installs`;
    }
    if (thresholds.Sales != null && thresholds.Installs != null) {
      return `${thresholds.Sales} Sales + ${thresholds.Installs} Installs`;
    }
    if (thresholds.Installs != null) {
      return `${thresholds.Installs} Installs`;
    }
    return '';
  }

  if (thresholds.Installs != null) {
    return `${thresholds.Installs} Installs`;
  }

  return '';
};

const getLevelLabels = (
  orgType: NewDashboardOrgType,
  sellsSunrun: boolean,
): string[] => {
  const rankMap = getNextRankMap(orgType, sellsSunrun);
  return getRankLevels(orgType, sellsSunrun).map((level) =>
    formatLevelLabel(rankMap[level], level, orgType),
  );
};

export type { RunXRankThresholds };
export { getLevelLabels, getMaxRankLevel, getNextRankMap };
